import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { PhotoIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

import apiService from '../../services/api';
import { NubieAiMedia } from '../../types/api';
import Logger from '../../utils/logger';
import { useApercusMedias } from './useApercusMedias';

/**
 * Les photos d'un site vitrine : televerser, placer dans un cadre, decrire, retirer.
 *
 * POURQUOI CE PANNEAU EXISTE. Le modele ne genere AUCUNE image -- `codestral-2508` ne
 * declare meme pas `vision` -- et le socle attend les photos depuis le lot 2 :
 * `.nubie-emplacement` est un cadre 4/3 qui montre un motif pointille quand il est vide.
 * Sans cet ecran, chaque site reste decore de cadres vides et le client n'a aucun moyen
 * d'y mettre ses propres photos.
 *
 * L'APERCU PASSE PAR UN BLOB, PAS PAR UN `src`. L'original vit dans un bucket PRIVE,
 * sous un prefixe que le proxy nginx ne sert pas : il n'existe aucune URL publique avant
 * publication. L'API sert donc l'apercu -- mais elle est sur une AUTRE ORIGINE que la
 * console, et une balise `<img src>` ne porterait pas le cookie httpOnly. On passe par
 * le client axios, qui envoie les identifiants, et on revoque les URL d'objet : sans ca
 * chaque affichage retiendrait ses octets jusqu'au rechargement de la page.
 *
 * LE RE-ENCODAGE EST FAIT PAR L'API, et il faut le dire au client : la photo publiee
 * n'est pas celle qu'il a envoyee. Elle est reduite, convertie en WebP, et ses
 * metadonnees sont purgees -- dont les COORDONNEES GPS que porte toute photo de
 * telephone. C'est une protection, pas une alteration subie, d'ou le texte d'aide.
 */

/**
 * `useTranslation` caste comme dans `NubieAIPage` : c'est la FONCTION qu'on caste, pas
 * son resultat, et c'est ce qui court-circuite l'instanciation au site d'appel.
 *
 * Sans ca, l'inference generique de react-i18next fait deborder la limite de recursion
 * de TypeScript (TS2589) -- constate ici des la premiere compilation. `i18next.d.ts`
 * declare deja `resources: any`, donc le typage des cles n'est garanti nulle part dans
 * ce depot : ce cast ne renonce a rien.
 */
const traduire = useTranslation as unknown as (
  ns: string
) => { t: (cle: string, options?: Record<string, unknown>) => string };

interface Props {
  siteId: string;
  /** Les emplacements declares par la page, lus par l'API DANS le HTML. */
  emplacements: string[];
  medias: NubieAiMedia[];
  /** Appele apres toute modification, pour que la page relise la fiche du site. */
  onChange: () => Promise<void> | void;
}

const PhotosPanel: React.FC<Props> = ({ siteId, emplacements, medias, onChange }) => {
  const { t } = traduire('nubieai');

  const [envoi, setEnvoi] = useState(false);
  const [occupe, setOccupe] = useState<string | null>(null);
  const entree = useRef<HTMLInputElement>(null);

  // LE MEME CROCHET QUE L'APERCU DE LA PAGE. Les deux ont besoin des memes octets :
  // chacun les telechargeant pour son compte, chaque photo passerait deux fois sur le
  // reseau et serait retenue deux fois en memoire.
  const apercus = useApercusMedias(siteId, medias);

  const erreur = useCallback(
    (err: unknown, repli: string) => {
      const e = err as { response?: { status?: number; data?: { detail?: unknown } } };
      const detail =
        typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : null;
      const statut = e?.response?.status;
      Logger.error('NubieAI: photo', err);
      // 413 et 422 portent un message ECRIT POUR L'ECRAN par l'API -- taille, format
      // refuse, quota atteint. Il est plus precis que tout repli generique.
      if ((statut === 413 || statut === 422) && detail) {
        toast.error(detail);
      } else {
        toast.error(String(t(repli)));
      }
    },
    [t]
  );

  const televerser = async (fichier: File) => {
    if (envoi) return;
    setEnvoi(true);
    try {
      await apiService.uploadNubieAiMedia(siteId, fichier);
      await onChange();
      toast.success(String(t('photos.ajoutee')));
    } catch (err) {
      erreur(err, 'photos.erreurAjout');
    } finally {
      setEnvoi(false);
      // Remettre l'entree a zero : sans ca, reselectionner LE MEME fichier ne
      // declenche aucun `change` et le client croit l'interface cassee.
      if (entree.current) entree.current.value = '';
    }
  };

  const placer = async (media: NubieAiMedia, slot: string) => {
    setOccupe(media.id);
    try {
      // Chaine VIDE et non `null` : cote API, `slot` absent ne touche a rien et `slot`
      // vide retire la photo de son emplacement. Les deux doivent rester distincts.
      await apiService.updateNubieAiMedia(siteId, media.id, { slot });
      await onChange();
    } catch (err) {
      erreur(err, 'photos.erreurPlacement');
    } finally {
      setOccupe(null);
    }
  };

  const decrire = async (media: NubieAiMedia, alt: string) => {
    if (alt === media.alt) return;
    setOccupe(media.id);
    try {
      await apiService.updateNubieAiMedia(siteId, media.id, { alt });
      await onChange();
    } catch (err) {
      erreur(err, 'photos.erreurPlacement');
    } finally {
      setOccupe(null);
    }
  };

  const retirer = async (media: NubieAiMedia) => {
    // `window.confirm` : la suppression est IRREVERSIBLE, l'original est efface du
    // stockage. C'est le seul geste de ce panneau qui ne se rattrape pas.
    if (!window.confirm(String(t('photos.confirmerSuppression')))) return;
    setOccupe(media.id);
    try {
      await apiService.deleteNubieAiMedia(siteId, media.id);
      await onChange();
    } catch (err) {
      erreur(err, 'photos.erreurSuppression');
    } finally {
      setOccupe(null);
    }
  };

  const sansEmplacement = emplacements.length === 0;

  return (
    <div className="border-t border-border-1 pt-4">
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-primary mb-1">
        <PhotoIcon className="w-4 h-4" strokeWidth={1.8} />
        {t('photos.titre')}
      </h3>
      <p className="text-xs text-text-secondary">{t('photos.aide')}</p>

      {/* LE RE-ENCODAGE EST ANNONCE. La photo publiee n'est pas celle qu'il envoie :
          elle est reduite et ses metadonnees purgees, coordonnees GPS comprises. Le
          taire laisserait croire a une alteration subie. */}
      <p className="mt-1 text-xs text-text-tertiary">{t('photos.aideTraitement')}</p>

      {sansEmplacement && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
          {t('photos.aucunEmplacement')}
        </p>
      )}

      <div className="mt-3">
        <input
          ref={entree}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void televerser(f);
          }}
        />
        <button
          type="button"
          onClick={() => entree.current?.click()}
          disabled={envoi}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-surface-2 text-text-primary border border-border-1 rounded-button text-sm hover:border-border-2 disabled:opacity-40 transition-colors"
        >
          <ArrowUpTrayIcon className="w-4 h-4" strokeWidth={1.8} />
          {envoi ? t('photos.envoiEnCours') : t('photos.ajouter')}
        </button>
      </div>

      {medias.length > 0 && (
        <ul className="mt-3 space-y-3">
          {medias.map((m) => (
            <li key={m.id} className="flex gap-3">
              <div className="w-20 flex-shrink-0 aspect-[4/3] rounded-input overflow-hidden bg-surface-2 border border-border-1">
                {apercus[m.id] ? (
                  <img
                    src={apercus[m.id]}
                    alt={m.alt || String(t('photos.sansDescription'))}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PhotoIcon className="w-5 h-5 text-text-quaternary" strokeWidth={1.5} />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0 space-y-1.5">
                <select
                  value={m.slot ?? ''}
                  disabled={occupe === m.id || sansEmplacement}
                  onChange={(e) => void placer(m, e.target.value)}
                  className="w-full px-2 py-1.5 bg-surface-0 border border-border-1 rounded-input text-xs text-text-primary focus:outline-none focus:border-accent disabled:opacity-50"
                >
                  <option value="">{t('photos.nonPlacee')}</option>
                  {emplacements.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  defaultValue={m.alt}
                  maxLength={300}
                  disabled={occupe === m.id}
                  placeholder={String(t('photos.descriptionPlaceholder'))}
                  onBlur={(e) => void decrire(m, e.target.value)}
                  className="w-full px-2 py-1.5 bg-surface-0 border border-border-1 rounded-input text-xs text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent disabled:opacity-50"
                />

                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-quaternary">
                    {m.largeur} × {m.hauteur} · {Math.round(m.octets / 1024)} Ko
                  </span>
                  <button
                    type="button"
                    onClick={() => void retirer(m)}
                    disabled={occupe === m.id}
                    className="flex items-center gap-1 text-xs text-text-tertiary hover:text-red-500 disabled:opacity-40 transition-colors"
                  >
                    <TrashIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                    {t('photos.retirer')}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PhotosPanel;
